"use client"

// Right-side settings panel for editing the user's steering preferences.
//
// Loads the caller's preferences on open, edits a local draft, and PUTs the
// whole document on save. A master switch activates the preferences; when off
// the agent ignores them. Structured toggles are single-select chip groups
// (clicking the active chip clears it back to "no preference"); notes are a
// bounded textarea. On save the panel confirms inline that the change applies on
// the user's next message (the runtime reads preferences per turn).

import { useEffect, useState, type ReactNode } from "react"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { copy } from "@/lib/copy"
import { usePreferences } from "@/hooks/usePreferences"
import {
  DESAMBIGUACION_OPTIONS,
  emptyPreferences,
  FORMATO_OPTIONS,
  LONGITUD_OPTIONS,
  NOMBRE_MAX,
  NOTAS_LIBRES_MAX,
  type PrefOption,
  TONO_OPTIONS,
  type UserPreferences,
} from "@/types/preferences"

interface PreferencesSheetProps {
  /** Whether the panel is open. */
  open: boolean
  /** Open-state change handler (close on overlay/esc/X). */
  onOpenChange: (open: boolean) => void
}

/** A labelled field wrapper. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <span className="text-sm font-medium text-foreground">{label}</span>
      {children}
    </div>
  )
}

/** A titled section: an uppercase header with clear separation from its fields. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      <div className="space-y-4">{children}</div>
    </section>
  )
}

/** An on/off switch. */
function Toggle({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  ariaLabel: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
        checked ? "bg-primary" : "bg-muted"
      )}
    >
      <span
        className={cn(
          "inline-block size-5 transform rounded-full bg-background shadow transition-transform",
          checked ? "translate-x-5" : "translate-x-0.5"
        )}
      />
    </button>
  )
}

/** Single-select chip group; clicking the active chip clears the value. */
function SingleChips<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: PrefOption<T>[]
  value: T | null
  onChange: (v: T | null) => void
  ariaLabel: string
}) {
  return (
    <div role="group" aria-label={ariaLabel} className="flex flex-wrap gap-2">
      {options.map(opt => {
        const active = value === opt.value
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(active ? null : opt.value)}
            className={cn(
              "rounded-full border px-3 py-1 text-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
              active
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border text-foreground hover:bg-accent"
            )}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

/**
 * The preferences editing panel.
 *
 * @param open - whether the panel is shown.
 * @param onOpenChange - open-state change handler.
 */
export function PreferencesSheet({ open, onOpenChange }: PreferencesSheetProps) {
  const { load, save, status, error } = usePreferences()
  const [draft, setDraft] = useState<UserPreferences>(emptyPreferences())
  const [loaded, setLoaded] = useState(false)

  // Load the current preferences each time the panel opens; fall back to empty
  // defaults on error so the form is always editable.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoaded(false)
    load()
      .then(p => {
        if (!cancelled) {
          setDraft(p)
          setLoaded(true)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDraft(emptyPreferences())
          setLoaded(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [open, load])

  function set<K extends keyof UserPreferences>(key: K, value: UserPreferences[K]): void {
    setDraft(d => ({ ...d, [key]: value }))
  }

  const handleSave = async () => {
    try {
      await save(draft)
    } catch {
      // status is set to "error" by the hook; the footer surfaces it.
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 sm:max-w-none md:max-w-md">
        <SheetHeader>
          <SheetTitle>{copy.preferences.title}</SheetTitle>
          <SheetDescription>{copy.preferences.description}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-8 overflow-y-auto px-4 py-2">
          {/* Master switch + steering disclaimer */}
          <div className="space-y-2 rounded-md border border-border p-3">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium text-foreground">
                {copy.preferences.activar}
              </span>
              <Toggle
                checked={draft.activo}
                onChange={v => set("activo", v)}
                ariaLabel={copy.preferences.activar}
              />
            </div>
            <p className="text-xs text-muted-foreground">{copy.preferences.disclaimer}</p>
          </div>

          {/* Presentation & interaction */}
          <Section title={copy.preferences.sectionPresentation}>
            <Field label={copy.preferences.nombre}>
              <Input
                value={draft.nombre ?? ""}
                onChange={e => set("nombre", e.target.value || null)}
                placeholder={copy.preferences.nombrePlaceholder}
                maxLength={NOMBRE_MAX}
              />
            </Field>
            <Field label={copy.preferences.tono}>
              <SingleChips
                options={TONO_OPTIONS}
                value={draft.tono}
                onChange={v => set("tono", v)}
                ariaLabel={copy.preferences.tono}
              />
            </Field>
            <Field label={copy.preferences.formato}>
              <SingleChips
                options={FORMATO_OPTIONS}
                value={draft.formatoRespuesta}
                onChange={v => set("formatoRespuesta", v)}
                ariaLabel={copy.preferences.formato}
              />
            </Field>
            <Field label={copy.preferences.longitud}>
              <SingleChips
                options={LONGITUD_OPTIONS}
                value={draft.longitudRespuesta}
                onChange={v => set("longitudRespuesta", v)}
                ariaLabel={copy.preferences.longitud}
              />
            </Field>
            <Field label={copy.preferences.desambiguacion}>
              <SingleChips
                options={DESAMBIGUACION_OPTIONS}
                value={draft.desambiguacion}
                onChange={v => set("desambiguacion", v)}
                ariaLabel={copy.preferences.desambiguacion}
              />
            </Field>
          </Section>

          {/* Free-text notes */}
          <Section title={copy.preferences.sectionNotes}>
            <Field label={copy.preferences.notas}>
              <textarea
                value={draft.notasLibres ?? ""}
                onChange={e => set("notasLibres", e.target.value || null)}
                rows={4}
                maxLength={NOTAS_LIBRES_MAX}
                placeholder={copy.preferences.notasPlaceholder}
                className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <div className="text-right text-xs text-muted-foreground">
                {(draft.notasLibres ?? "").length} / {NOTAS_LIBRES_MAX}
              </div>
            </Field>
          </Section>
        </div>

        <SheetFooter>
          {status === "saved" && (
            <p className="text-xs text-emerald-600 dark:text-emerald-400">
              {copy.preferences.saved}
            </p>
          )}
          {status === "error" && (
            <p className="text-xs text-destructive">{error ?? copy.preferences.saveError}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setDraft(emptyPreferences())}>
              {copy.preferences.reset}
            </Button>
            <Button type="button" onClick={handleSave} disabled={status === "saving" || !loaded}>
              {status === "saving" ? copy.preferences.saving : copy.preferences.save}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
